import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SampleModal } from './SampleModal';
import { regionSlug } from '../../lib/regions';
import { ArrowRight, CreditCard, BadgeCheck, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const imgSrc = (u) => (!u ? '' : u.startsWith('http') ? u : `${BACKEND_URL}${u}`);
// Shown while a studio cover is unavailable (e.g. during a studio move).
export const coverFallback = (e) => {
  const el = e.currentTarget;
  if (el.dataset.fallback) return;
  el.dataset.fallback = '1';
  el.src = '/og-logo.png';
  el.classList.remove('object-cover');
  el.classList.add('object-contain', 'p-10', 'bg-[#F5EFE9]');
};

export const UpdatedBadge = ({ fees, className = '' }) => (fees?.updated_this_month ? (
  <span data-testid="updated-this-month-badge" className={`inline-flex items-center gap-1 bg-[#C5A059] text-[#2D1037] text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${className}`}>
    <BadgeCheck size={12} /> Updated this month
  </span>
) : null);

export const SavingsTag = ({ product, className = '' }) => (product.savings_aud > 0 ? (
  <span data-testid={`bundle-savings-${product.id}`} className={`inline-flex items-center rounded-full bg-[#3B164C]/10 text-[#3B164C] text-xs font-semibold px-3 py-1 ${className}`}>
    Save A${product.savings_aud} vs buying separately
  </span>
) : null);

export const ProductCard = ({ product, onBuy }) => {
  const isGuide = product.source === 'guide_studio';
  const [sample, setSample] = useState(null);
  const marketing = product.fees?.marketing_lines?.[0];
  return (
    <div className="card-hover group flex flex-col" data-testid={`product-card-${product.id}`}>
      <Link to={`/shop/p/${product.id}`} className="relative h-56 bg-[#2D1037] overflow-hidden block">
        {product.image_url ? (
          <img src={imgSrc(product.image_url)} onError={coverFallback} alt={product.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#3B164C] to-[#2D1037] flex items-center justify-center">
            <span className="font-display text-5xl text-[#C5A059]">{product.title?.[0] || 'E'}</span>
          </div>
        )}
        <div className="absolute top-4 left-4 flex flex-wrap gap-2 z-10">
          {product.featured && <span className="bg-[#FAF7F2] text-[#3B164C] text-[10px] font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full">Best value</span>}
          <UpdatedBadge fees={product.fees} />
        </div>
      </Link>
      <div className="p-7 flex flex-col flex-grow">
        {isGuide ? (
          <Link to={`/destinations/${regionSlug(product.destination)}`} data-testid={`product-destination-${product.id}`} className="text-xs text-[#C5A059] font-semibold uppercase tracking-[0.2em] hover:text-[#3B164C]">
            {product.origin} → {product.destination}
          </Link>
        ) : <span className="text-xs text-[#C5A059] font-semibold uppercase tracking-[0.2em]">{product.category}</span>}
        <Link to={`/shop/p/${product.id}`}>
          <h3 className="font-display text-2xl font-medium text-[#1E1622] mt-2 mb-1 leading-snug hover:text-[#3B164C]">{product.title}</h3>
        </Link>
        {isGuide && <p className="text-xs text-[#1E1622]/50 italic mb-3">{product.guide_title}</p>}
        <p className="text-sm text-[#1E1622]/60 mb-5 line-clamp-3 font-light flex-grow">{product.tagline || product.description}</p>
        {product.sample_url && (
          <button onClick={() => setSample(product)} data-testid={`product-sample-${product.id}`}
            className="self-start text-sm text-[#3B164C] hover:text-[#C5A059] inline-flex items-center gap-1 mb-5 transition-colors duration-200"><FileText size={14} /> Read a free sample</button>
        )}
        <div className="pt-5 border-t border-[#C5A059]/20">
          <div className="flex items-center justify-between">
            <span className="font-display text-2xl font-medium text-[#3B164C]" data-testid={`product-price-${product.id}`}>{product.price === 0 ? 'Free' : `A$${product.price}`}</span>
            {product.price === 0 ? (
              <Link to="/freebie" data-testid={`product-link-${product.id}`} className="inline-flex items-center gap-2 text-[#3B164C] font-medium text-sm">Get Free <ArrowRight size={16} /></Link>
            ) : (
              <button onClick={() => onBuy(product)} data-testid={`product-buy-${product.id}`}
                className="inline-flex items-center gap-2 bg-[#3B164C] text-white rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 hover:bg-[#2D1037]">
                Buy Now <CreditCard size={14} />
              </button>
            )}
          </div>
          <SavingsTag product={product} className="mt-3" />
          {marketing && <p className="text-xs text-[#1E1622]/50 mt-2" data-testid={`product-marketing-${product.id}`}>{marketing}</p>}
        </div>
      </div>
      <SampleModal product={sample} onClose={() => setSample(null)} />
    </div>
  );
};
